import { z } from "zod";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema } from "./source.js";
export const COST_TELEMETRY_SCHEMA_VERSION = "cost-telemetry.v1";
export const CostTelemetryOperationSchema = z.enum([
    "specify-run",
    "pdf-parse",
    "pasted-text-parse",
    "full-policy-derive",
    "focused-re-check",
    "schedule-generation",
    "document-parse",
    "tender-classification",
    "engine-stage",
    "usage-pending",
    "other"
]);
export const CostTelemetryCurrencyAmountSchema = z
    .object({
    value: z.number().nonnegative(),
    currency: z.enum(["GBP", "USD"]),
    formatted: NonEmptyStringSchema
})
    .strict();
export const CostTelemetryPricingBasisSchema = z
    .object({
    version: NonEmptyStringSchema,
    provider: NonEmptyStringSchema,
    modelId: NonEmptyStringSchema,
    sourceUrl: NonEmptyStringSchema,
    sourceRetrievedAt: IsoTimestampSchema,
    inputUsdPerMillion: z.number().nonnegative(),
    outputUsdPerMillion: z.number().nonnegative(),
    cachedInputUsdPerMillion: z.number().nonnegative().optional(),
    longContextThresholdTokens: z.number().int().positive().optional(),
    longContextInputUsdPerMillion: z.number().nonnegative().optional(),
    longContextCachedInputUsdPerMillion: z.number().nonnegative().optional(),
    longContextOutputUsdPerMillion: z.number().nonnegative().optional(),
    gbpToUsdRate: z.number().positive(),
    estimateLabel: NonEmptyStringSchema
})
    .strict();
export const CostTelemetryRollupSchema = z
    .object({
    operationId: CostTelemetryOperationSchema,
    label: NonEmptyStringSchema,
    callCount: NonNegativeIntegerSchema,
    promptTokens: NonNegativeIntegerSchema,
    completionTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    estimatedUsd: CostTelemetryCurrencyAmountSchema,
    estimatedGbp: CostTelemetryCurrencyAmountSchema,
    estimateText: NonEmptyStringSchema
})
    .strict();
export const CostTelemetryProviderRollupSchema = z
    .object({
    provider: NonEmptyStringSchema,
    modelId: NonEmptyStringSchema,
    status: z.enum(["recorded", "missing_usage", "mixed"]),
    callCount: NonNegativeIntegerSchema,
    missingUsageCount: NonNegativeIntegerSchema,
    promptTokens: NonNegativeIntegerSchema,
    completionTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    estimatedUsd: CostTelemetryCurrencyAmountSchema,
    estimatedGbp: CostTelemetryCurrencyAmountSchema,
    estimateText: NonEmptyStringSchema,
    missingUsageReasons: z.array(NonEmptyStringSchema).optional()
})
    .strict();
export const CostTelemetryCallClassRollupSchema = z
    .object({
    operationId: CostTelemetryOperationSchema,
    callClass: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    status: z.enum(["recorded", "missing_usage", "mixed"]),
    callCount: NonNegativeIntegerSchema,
    missingUsageCount: NonNegativeIntegerSchema,
    promptTokens: NonNegativeIntegerSchema,
    completionTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    estimatedUsd: CostTelemetryCurrencyAmountSchema,
    estimatedGbp: CostTelemetryCurrencyAmountSchema,
    estimateText: NonEmptyStringSchema,
    providers: z.array(CostTelemetryProviderRollupSchema),
    missingUsageReasons: z.array(NonEmptyStringSchema).optional()
})
    .strict();
export const CostTelemetryBudgetSummarySchema = z
    .object({
    policyId: NonEmptyStringSchema,
    label: NonEmptyStringSchema,
    limitGbp: CostTelemetryCurrencyAmountSchema,
    consumedGbp: CostTelemetryCurrencyAmountSchema,
    remainingGbp: CostTelemetryCurrencyAmountSchema,
    consumedUsd: CostTelemetryCurrencyAmountSchema,
    remainingUsd: CostTelemetryCurrencyAmountSchema,
    percentConsumed: z.number().min(0),
    status: z.enum(["within_budget", "exhausted"]),
    source: NonEmptyStringSchema
})
    .strict();
export const CostTelemetryLedgerTotalsSchema = z
    .object({
    callCount: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    missingUsageCount: NonNegativeIntegerSchema,
    unmeteredFailureCount: NonNegativeIntegerSchema
})
    .strict();
export const CostTelemetryReconciliationSchema = z
    .object({
    status: z.enum(["reconciled", "gap", "process_unavailable"]),
    durableProjectLedger: CostTelemetryLedgerTotalsSchema,
    processProjectLedger: CostTelemetryLedgerTotalsSchema.nullable(),
    processWindowStartedAt: IsoTimestampSchema.nullable(),
    callCountGap: z.number().int(),
    totalTokensGap: z.number().int(),
    message: NonEmptyStringSchema
})
    .strict();
/**
 * One provider:model pricing basis actually used while computing this
 * response's figures. `pricingBasis` (below) stays the single active pair's
 * basis for backward compatibility; `pricingBases` is additive and lets a
 * ledger spanning more than one provider/model pair (e.g. mid-migration from
 * Gemini to OpenAI) show every basis that contributed, not just one.
 */
export const CostTelemetryDailyProviderRollupSchema = z
    .object({
    date: NonEmptyStringSchema,
    provider: NonEmptyStringSchema,
    modelId: NonEmptyStringSchema,
    callCount: NonNegativeIntegerSchema,
    promptTokens: NonNegativeIntegerSchema,
    completionTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    estimatedUsd: CostTelemetryCurrencyAmountSchema,
    estimatedGbp: CostTelemetryCurrencyAmountSchema
})
    .strict();
export const CostTelemetryEngineStageSchema = z
    .object({
    runId: IdSchema,
    projectId: IdSchema,
    stageId: NonEmptyStringSchema,
    provider: NonEmptyStringSchema,
    modelId: NonEmptyStringSchema,
    status: z.enum(["measured", "unmeasured"]),
    inputTokens: NonNegativeIntegerSchema.nullable(),
    outputTokens: NonNegativeIntegerSchema.nullable(),
    completedCalls: NonNegativeIntegerSchema.nullable(),
    reportedCostUsd: CostTelemetryCurrencyAmountSchema.optional(),
    reportedCostGbp: CostTelemetryCurrencyAmountSchema.optional(),
    engineUsdToGbp: z.number().positive().optional(),
    platformUsdToGbp: z.number().positive(),
    conversionDiscrepancy: NonEmptyStringSchema.optional(),
    artifactPath: NonEmptyStringSchema.optional(),
    artifactSha256: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
    reason: NonEmptyStringSchema.optional()
})
    .strict();
export const ProjectCostTelemetryResponseSchema = z
    .object({
    schemaVersion: z.literal(COST_TELEMETRY_SCHEMA_VERSION),
    projectId: IdSchema,
    generatedAt: IsoTimestampSchema,
    pricingBasis: CostTelemetryPricingBasisSchema,
    total: CostTelemetryRollupSchema,
    operations: z.array(CostTelemetryRollupSchema),
    callClasses: z.array(CostTelemetryCallClassRollupSchema),
    reconciliation: CostTelemetryReconciliationSchema,
    budgets: z.array(CostTelemetryBudgetSummarySchema),
    estimateDisclaimer: NonEmptyStringSchema,
    engineStages: z.array(CostTelemetryEngineStageSchema).optional(),
    /** Additive (S8): every distinct pricing basis actually applied. */
    pricingBases: z.array(CostTelemetryPricingBasisSchema).optional(),
    /** Additive (S8): per-provider:model per-day rollups, oldest first. */
    dailyProviderRollups: z.array(CostTelemetryDailyProviderRollupSchema).optional(),
    /**
     * Additive (S8 round 2, AMBER fix): count of ledger entries silently
     * excluded from `dailyProviderRollups` because their timestamp could not
     * be parsed into a date bucket. `dailyProviderRollups` was previously
     * "additive/best-effort" with no way to tell an all-dated ledger apart
     * from one where every entry was silently dropped; this makes the drop
     * count visible instead of just swallowing it.
     */
    dailyProviderRollupsSkippedUndatedCount: NonNegativeIntegerSchema.optional()
})
    .strict();
//# sourceMappingURL=costTelemetry.js.map